// Utility functions
const getPageByNumber = (pages, pageNumber) => {
  return pages.find((page) => page.number === pageNumber);
};

const initialState = {
  custom_form: {
    static_page_descriptions: {},
    pages: [],
    member_type_properties: [],
    visible_member_type_properties: [],
    save_status: false,
  },
  deleted_pages: [],
  member_type_properties: [],
  visible_member_type_properties: [],
};

const updatePageNumbers = (pages) => {
  return pages.map((page, index) => ({
    ...page,
    number: index + 1,
  }));
};

const getCurrentFieldIds = (pages) => {
  if (!pages || !Array.isArray(pages)) {
    return [];
  }
  const fieldIds = pages.map((page) => page.fields || []).flat();
  return [...new Set(fieldIds)];
};

const updateVisibleMemberTypeProperties = (memberTypeProperties, pages) => {
  if (!memberTypeProperties || !Array.isArray(memberTypeProperties)) {
    return [];
  }
  const currentFieldIds = getCurrentFieldIds(pages);
  return memberTypeProperties.filter(
    (mtp) => !currentFieldIds.includes(mtp.id)
  );
};

const customFormReducer = (state = initialState, action) => {
  switch (action.type) {
    case "CUSTOM_FORM_SET": {
      const customForm = { ...action.payload };
      
      // Process pages - ensure pages exists and is an array
      if (!customForm.pages || !Array.isArray(customForm.pages)) {
        customForm.pages = [];
      }
      
      customForm.pages = customForm.pages.map((page) => ({
        ...page,
        fields: page.field_ids ? page.field_ids.map((field) => parseInt(field)) : [],
      }));
      
      customForm.pages = [...customForm.pages].sort((a, b) => a.number - b.number);
      
      // Ensure static_page_descriptions exists
      if (!customForm.static_page_descriptions) {
        customForm.static_page_descriptions = {};
      }
      
      const visibleMemberTypeProperties = updateVisibleMemberTypeProperties(
        state.member_type_properties,
        customForm.pages
      );

      return {
        ...state,
        custom_form: customForm,
        visible_member_type_properties: visibleMemberTypeProperties,
      };
    }

    case "CUSTOM_FORM_UPDATE_FIELD_POSITION": {
      const { pageNumber, fieldId, position } = action.payload;
      let newPages = state.custom_form.pages.map((page) => ({
        ...page,
        fields: page.fields.filter((f) => f !== fieldId),
      }));

      const targetPage = getPageByNumber(newPages, pageNumber);
      if (targetPage) {
        targetPage.fields.splice(position, 0, fieldId);
      }

      const visibleMemberTypeProperties = updateVisibleMemberTypeProperties(
        state.member_type_properties,
        newPages
      );

      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          pages: newPages,
        },
        visible_member_type_properties: visibleMemberTypeProperties,
      };
    }

    case "CUSTOM_FORM_REMOVE_FIELD": {
      const { pageNumber, fieldId } = action.payload;
      const newPages = state.custom_form.pages.map((page) => {
        if (page.number === pageNumber) {
          return {
            ...page,
            fields: page.fields.filter((f) => f !== fieldId),
          };
        }
        return page;
      });

      const visibleMemberTypeProperties = updateVisibleMemberTypeProperties(
        state.member_type_properties,
        newPages
      );

      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          pages: newPages,
        },
        visible_member_type_properties: visibleMemberTypeProperties,
      };
    }

    case "CUSTOM_FORM_UPDATE_PAGE_DESCRIPTION": {
      const { pageNumber, locale, description } = action.payload;
      const newPages = state.custom_form.pages.map((page) => {
        if (page.number === pageNumber) {
          return {
            ...page,
            [`description_${locale}`]: description,
          };
        }
        return page;
      });

      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          pages: newPages,
        },
      };
    }

    case "CUSTOM_FORM_UPDATE_STATIC_PAGE_DESCRIPTION": {
      const { key, locale, description } = action.payload;
      const newStaticPageDescriptions = {
        ...state.custom_form.static_page_descriptions,
        [key]: {
          ...state.custom_form.static_page_descriptions[key],
          [locale]: description,
        },
      };

      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          static_page_descriptions: newStaticPageDescriptions,
        },
      };
    }

    case "CUSTOM_FORM_ADD_PAGE": {
      const newPageNumber = state.custom_form.pages.length + 1;
      const newPage = {
        number: newPageNumber,
        description: "",
        fields: [],
      };

      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          pages: [...state.custom_form.pages, newPage],
        },
      };
    }

    case "CUSTOM_FORM_REMOVE_PAGE": {
      const pageNumber = action.payload;
      let removedPage = null;
      
      const newPages = state.custom_form.pages.filter((page) => {
        if (page.number === pageNumber) {
          removedPage = { ...page, _destroy: 1 };
          return false;
        }
        return true;
      });

      const updatedPages = updatePageNumbers(newPages);
      const newDeletedPages = removedPage 
        ? [...state.deleted_pages, removedPage]
        : state.deleted_pages;

      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          pages: updatedPages,
        },
        deleted_pages: newDeletedPages,
      };
    }

    case "CUSTOM_FORM_SET_MEMBER_TYPE_PROPERTIES": {
      const memberTypeProperties = typeof action.payload === 'string' 
        ? JSON.parse(action.payload) 
        : action.payload;

      const visibleMemberTypeProperties = updateVisibleMemberTypeProperties(
        memberTypeProperties,
        state.custom_form.pages
      );

      return {
        ...state,
        member_type_properties: memberTypeProperties,
        visible_member_type_properties: visibleMemberTypeProperties,
      };
    }

    case "CUSTOM_FORM_SET_SAVE_STATUS": {
      return {
        ...state,
        custom_form: {
          ...state.custom_form,
          save_status: action.payload,
        },
      };
    }

    default:
      return state;
  }
};

export default customFormReducer;
